import { api } from '../../lib/api';

export type ApplicationStatus = 'pending' | 'approved' | 'declined' | 'withdrawn';

export interface ApplicationGenre {
  id: number;
  name: string;
  slug: string;
}

export interface MyApplication {
  id: number;
  status: ApplicationStatus;
  country: string;
  timezone: string;
  pitch: string;
  experience: string | null;
  genres: ApplicationGenre[];
  decision_note: string | null;
  decided_at: string | null;
  created_at: string;
}

export interface MyApplicationState {
  /**
   * Read from the permission, not from the application's status.
   *
   * An approval that was later revoked leaves an `approved` row behind, so a
   * page that trusted the row would offer a control panel the API refuses.
   */
  is_host: boolean;
  application: MyApplication | null;
}

export interface ApplicationDraft {
  country: string;
  timezone: string;
  pitch: string;
  experience: string | null;
  genres: number[];
  rights_ack: boolean;
}

export const hosting = {
  mine: () => api.get<MyApplicationState>('/host/application'),
  apply: (draft: ApplicationDraft) => api.post<{ application: MyApplication }>('/host/applications', draft),
  withdraw: (id: number) => api.delete(`/host/applications/${id}`),
};

/* -------------------------------------------------------------------------- */
/* Countries and timezones                                                     */
/* -------------------------------------------------------------------------- */

export interface HostOptions {
  /** ISO 3166-1 alpha-2 codes, sorted. Names are rendered locally. */
  countries: string[];
  timezones: string[];
}

/**
 * The lists come from the server, and the *names* from the browser.
 *
 * Building the whole thing client-side is tempting - `Intl.DisplayNames` knows
 * every region and `Intl.supportedValuesOf('timeZone')` every zone, with no
 * round trip. It is wrong for a handful of people: the server accepts a country
 * only if PHP's tz database gives it a timezone, and `Intl.DisplayNames` names
 * regions that have none. Somebody from Bouvet Island would pick their country
 * off a list this site offered them and be told it does not exist.
 *
 * So the *set* is the server's, derived from the same data as the validator,
 * and `Intl.DisplayNames` turns each code into a name in the reader's own
 * language. Neither end holds a hand-maintained list.
 */
export const hostOptions = () => api.get<HostOptions>('/host/options');

/** The visitor's own zone, pre-selected so most people never touch the field. */
export function guessTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * "United Kingdom" from "GB".
 *
 * `fallback: 'none'` returns undefined for a code the engine does not know,
 * which is a cleaner test than comparing the result to the input - and the
 * code itself is a reasonable thing to show if it happens.
 */
let regionNames: Intl.DisplayNames | null = null;

export function countryName(code: string): string {
  try {
    regionNames ??= new Intl.DisplayNames(undefined, { type: 'region', fallback: 'none' });

    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/** Codes sorted by their displayed name, which is the order a reader scans. */
export function byName(codes: string[]): { code: string; name: string }[] {
  return codes
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** "Europe/London" reads better as "Europe / London". */
export function zoneLabel(zone: string): string {
  return zone.replace(/_/g, ' ').replace('/', ' / ');
}
